import React from 'react';
import { Link } from 'react-router-dom';
import { useCompare } from '../context/CompareContext';
import { X, ArrowRight } from 'lucide-react';

/**
 * Fixed-position bar that pins to the bottom of the viewport when the
 * user has queued at least one plan for side-by-side comparison. Shows
 * mini thumbnails, a "View comparison" CTA and a clear button.
 *
 * Rendered inside App.js at the tree root so it appears globally on
 * home-design pages. Auto-hides on /home-designs/compare itself.
 */

const CompareBar = () => {
  const { comparePlans, removeFromCompare, clearCompare, maxPlans, toastMessage } = useCompare();

  // Hide on the compare page itself
  if (typeof window !== 'undefined' && window.location.pathname === '/home-designs/compare') {
    return null;
  }

  if (!comparePlans.length) {
    return (
      // Even when empty, render a hidden container so the toast can
      // still appear (e.g. "already added").
      toastMessage ? (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[95] bg-[#1d1d1f] text-white text-sm px-4 py-2.5 rounded-full shadow-lg"
          role="status"
          data-testid="compare-toast"
        >
          {toastMessage}
        </div>
      ) : null
    );
  }

  return (
    <>
      {toastMessage && (
        <div
          className="fixed bottom-28 left-1/2 -translate-x-1/2 z-[96] bg-[#1d1d1f] text-white text-sm px-4 py-2.5 rounded-full shadow-lg"
          role="status"
          data-testid="compare-toast"
        >
          {toastMessage}
        </div>
      )}
      <div
        className="fixed bottom-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:right-auto z-[90] md:w-auto md:max-w-2xl bg-[#1d1d1f] text-white rounded-2xl shadow-2xl px-4 py-3 md:px-5 md:py-3 flex items-center gap-3"
        data-testid="compare-bar"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {comparePlans.map((p) => (
            <div key={p.full_slug} className="relative flex-shrink-0" data-testid={`compare-slot-${p.full_slug}`}>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg overflow-hidden bg-white/10 border border-white/20 flex items-center justify-center">
                {p.facade && !p.facade.includes('undefined') ? (
                  <img src={p.facade} alt={p.plan_name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                ) : (
                  <span className="text-[9px] text-white/60 leading-tight text-center px-1">{p.plan_name?.slice(0, 12)}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeFromCompare(p.full_slug)}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white text-[#1d1d1f] flex items-center justify-center hover:bg-[#f5f5f7]"
                aria-label={`Remove ${p.plan_name} from comparison`}
                data-testid={`compare-remove-${p.full_slug}`}
              >
                <X className="w-2.5 h-2.5" strokeWidth={3} />
              </button>
            </div>
          ))}
          {Array.from({ length: maxPlans - comparePlans.length }).map((_, i) => (
            <div
              key={`empty-${i}`}
              className="w-10 h-10 md:w-12 md:h-12 rounded-lg border border-dashed border-white/30 flex items-center justify-center text-[10px] text-white/40 flex-shrink-0"
              aria-hidden="true"
            >
              +
            </div>
          ))}
        </div>
        <div className="hidden md:block text-xs text-white/70 whitespace-nowrap">
          {comparePlans.length} of {maxPlans}
        </div>
        <Link
          to="/home-designs/compare"
          className="inline-flex items-center gap-1.5 bg-white text-[#1d1d1f] px-3 py-2 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-semibold hover:bg-[#f5f5f7] transition-colors whitespace-nowrap"
          data-testid="compare-bar-view-btn"
        >
          Compare
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <button
          type="button"
          onClick={clearCompare}
          className="text-white/60 hover:text-white p-1"
          aria-label="Clear comparison"
          data-testid="compare-bar-clear-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </>
  );
};

export default CompareBar;
